// ChecklistTemplateService.js

// Firebase
import { functions, httpsCallable } from 'firebaseConfig';
import { collection, deleteField, doc, getDoc, onSnapshot, query, setDoc, Timestamp, updateDoc, where } from 'firebase/firestore';
import { collections, db } from 'firebaseConfig';

// Utilities
import { generateKey } from 'utilities/Keys';

// Services
import { normalizeChecklistTemplateItems } from './checklistModels';

const CHECKLISTS_COLLECTION = collections.checklists;
const callable = (name) => httpsCallable(functions, name);

const normalizeString = (value) => String(value || '').trim();

function normalizeChecklistTemplateRow(row = {}) {
  const { description, sections, ...rest } = row || {};

  void description;
  void sections;

  return {
    ...rest,
    key: normalizeString(rest?.key || row?.id),
    items: normalizeChecklistTemplateItems(rest?.items || row?.sections || []),
  };
}

export async function createChecklistTemplate({
  key,
  workspaceKey,
  userKey,
  title,
  items = [],
  sections = [],
} = {}) {
  const trimmedWorkspaceKey = normalizeString(workspaceKey);
  const trimmedUserKey = normalizeString(userKey);
  const trimmedTitle = normalizeString(title);

  if (!trimmedWorkspaceKey || !trimmedUserKey || !trimmedTitle) return null;

  const checklistKey = normalizeString(key) || generateKey();
  const now = Timestamp.now();
  const payload = {
    key: checklistKey,
    workspaceKey: trimmedWorkspaceKey,
    title: trimmedTitle,
    titleLower: trimmedTitle.toLowerCase(),
    status: 'active',
    revision: 1,
    createdBy: trimmedUserKey,
    createdAt: now,
    updatedAt: now,
    items: normalizeChecklistTemplateItems(Array.isArray(items) && items.length > 0 ? items : sections),
  };

  await setDoc(doc(db, CHECKLISTS_COLLECTION, checklistKey), payload, { merge: false });

  return payload;
}

export async function updateChecklistTemplate(checklistKey, data = {}) {
  const trimmedChecklistKey = normalizeString(checklistKey);
  if (!trimmedChecklistKey) return;

  const payload = {
    updatedAt: Timestamp.now(),
    description: deleteField(),
  };

  if (Object.prototype.hasOwnProperty.call(data, 'title')) {
    const trimmedTitle = normalizeString(data.title);
    payload.title = trimmedTitle;
    payload.titleLower = trimmedTitle.toLowerCase();
  }

  if (Object.prototype.hasOwnProperty.call(data, 'items') || Object.prototype.hasOwnProperty.call(data, 'sections')) {
    payload.items = normalizeChecklistTemplateItems(
      Object.prototype.hasOwnProperty.call(data, 'items') ? data.items : data.sections
    );
    payload.sections = deleteField();
    payload.revision = Number.isFinite(Number(data?.revision))
      ? Number(data.revision)
      : undefined;
  }

  if (payload.revision === undefined) {
    delete payload.revision;
  }

  await updateDoc(doc(db, CHECKLISTS_COLLECTION, trimmedChecklistKey), payload);
}

export async function deleteChecklistTemplate({ workspaceKey, checklistKey } = {}) {
  const trimmedWorkspaceKey = normalizeString(workspaceKey);
  const trimmedChecklistKey = normalizeString(checklistKey);

  if (!trimmedWorkspaceKey || !trimmedChecklistKey) {
    throw new Error('workspaceKey and checklistKey are required');
  }

  const fn = callable('deleteChecklistTemplate');
  const { data } = await fn({
    workspaceKey: trimmedWorkspaceKey,
    checklistKey: trimmedChecklistKey,
  });

  return data || null;
}

export function subscribeToChecklistTemplates(workspaceKey, callback) {
  if (typeof callback !== 'function') {
    return () => {};
  }

  const trimmedWorkspaceKey = normalizeString(workspaceKey);
  if (!trimmedWorkspaceKey) {
    callback([]);
    return () => {};
  }

  const q = query(
    collection(db, CHECKLISTS_COLLECTION),
    where('workspaceKey', '==', trimmedWorkspaceKey)
  );

  return onSnapshot(
    q,
    (snapshot) => {
      const rows = snapshot.docs
        .map((docSnap) => normalizeChecklistTemplateRow({ id: docSnap.id, ...(docSnap.data() || {}) }))
        .sort((a, b) => {
          const aTitle = normalizeString(a?.titleLower || a?.title).toLowerCase();
          const bTitle = normalizeString(b?.titleLower || b?.title).toLowerCase();
          return aTitle.localeCompare(bTitle);
        });

      callback(rows);
    },
    (error) => {
      console.error('[ChecklistTemplateService] subscribeToChecklistTemplates error:', error);
      callback([]);
    }
  );
}

export function subscribeToChecklistTemplate(checklistKey, callback) {
  if (typeof callback !== 'function') {
    return () => {};
  }

  const trimmedChecklistKey = normalizeString(checklistKey);
  if (!trimmedChecklistKey) {
    callback(null);
    return () => {};
  }

  return onSnapshot(
    doc(db, CHECKLISTS_COLLECTION, trimmedChecklistKey),
    (docSnap) => {
      const row = docSnap.exists()
        ? normalizeChecklistTemplateRow({ id: docSnap.id, ...(docSnap.data() || {}), key: docSnap.id })
        : null;
      callback(row);
    },
    (error) => {
      console.error('[ChecklistTemplateService] subscribeToChecklistTemplate error:', error);
      callback(null);
    }
  );
}

export async function getChecklistTemplatesByKeys(workspaceKey, checklistKeys = []) {
  const trimmedWorkspaceKey = normalizeString(workspaceKey);
  if (!trimmedWorkspaceKey) return [];

  const normalizedKeys = [...new Set(
    (Array.isArray(checklistKeys) ? checklistKeys : [])
      .map((key) => normalizeString(key))
      .filter(Boolean)
  )];

  if (normalizedKeys.length === 0) return [];

  const snapshots = await Promise.all(
    normalizedKeys.map((key) => getDoc(doc(db, CHECKLISTS_COLLECTION, key)))
  );

  return snapshots
    .filter((snapshot) => snapshot.exists())
    .map((snapshot) => normalizeChecklistTemplateRow({ id: snapshot.id, ...(snapshot.data() || {}), key: snapshot.id }))
    .filter((row) => normalizeString(row?.workspaceKey) === trimmedWorkspaceKey);
}